import { useState, useEffect, useMemo, useRef } from "react";
import type {
  UseRouteDataParams,
  UseRouteDataReturn,
  RouteData,
  RouteMatrixElement,
} from "../types/routes";
import {
  createRouteKey,
  parseDuration,
  parseDistance,
  calculateHaversineDistance,
  formatCoordinatesForLogging,
  decodePolyline,
  validatePolyline,
  createPolylineKey,
} from "../utils/routeUtils";
import { routeCache, deduplicateRequest } from "../utils/routeCache";
import {
  makeRouteMatrixRequest,
  makeComputeRoutesRequest,
  GoogleRoutesError,
} from "../services/googleRoutesService";

// ===== MAIN HOOK =====
export const useRouteData = ({
  userLocation,
  pharmacyLocation,
  customerLocation,
  archived = false, // Skip API calls for archived orders
  includePolylines = false, // Fetch polyline data (only when map will be displayed)
}: UseRouteDataParams): UseRouteDataReturn => {
  // Suppress unused variable warning - userLocation kept for future extensibility
  void userLocation;
  const [routeData, setRouteData] = useState<RouteData>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Create stable route keys for deduplication
  const routeKey = useMemo(() => {
    if (!pharmacyLocation || !customerLocation) return null;
    return createRouteKey(pharmacyLocation, customerLocation);
  }, [pharmacyLocation, customerLocation]);

  const polylineKey = useMemo(() => {
    if (!pharmacyLocation || !customerLocation) return null;
    return createPolylineKey(pharmacyLocation, customerLocation);
  }, [pharmacyLocation, customerLocation]);

  useEffect(() => {
    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // CRITICAL: Skip all processing for archived orders
    if (archived) {
      console.log("[useRouteData] Archived order - skipping route calculation");
      setRouteData({});
      setLoading(false);
      setError(null);
      return;
    }

    // Skip if missing required data
    if (!pharmacyLocation || !customerLocation || !routeKey) {
      setRouteData({});
      setLoading(false);
      setError(null);
      return;
    }

    // Short distance optimization - skip API for very close locations
    const directDistance = calculateHaversineDistance(
      pharmacyLocation,
      customerLocation
    );
    if (directDistance < 100) {
      // Less than 100 meters
      console.log("[useRouteData] Short distance optimization (<100m):", {
        distance: Math.round(directDistance),
        routeKey: routeKey.substring(0, 30) + "...",
      });

      setRouteData({
        pharmacyToCustomer: {
          distance: `${Math.round(directDistance)} m`,
          duration: "< 1 min",
          shape: undefined,
        },
      });
      setLoading(false);
      setError(null);
      return;
    }

    // Check cache for both Matrix and polyline data
    const cachedMatrixResult = routeCache.get(
      routeKey
    ) as RouteMatrixElement | null;
    const cachedPolylineResult =
      includePolylines && polylineKey
        ? (routeCache.get(polylineKey) as {
            decodedCoordinates: [number, number][];
          } | null)
        : null;

    // Determine what data we need to fetch
    const needMatrixData = !cachedMatrixResult;
    const needPolylineData = includePolylines && !cachedPolylineResult;

    // If we have all the data we need from cache, return early
    if (!needMatrixData && (!includePolylines || !needPolylineData)) {
      console.log("[useRouteData] Complete cache hit:", {
        routeKey: routeKey.substring(0, 30) + "...",
        hasMatrix: !!cachedMatrixResult,
        hasPolylines: !!cachedPolylineResult,
      });

      const pharmacyToCustomer = {
        distance: cachedMatrixResult?.distanceMeters
          ? parseDistance(cachedMatrixResult.distanceMeters)
          : undefined,
        duration: cachedMatrixResult?.duration
          ? parseDuration(cachedMatrixResult.duration)
          : undefined,
        shape: cachedPolylineResult?.decodedCoordinates || undefined,
      };

      setRouteData({ pharmacyToCustomer });
      setLoading(false);
      setError(null);
      return;
    }

    // Make API requests (dual strategy: Matrix + optional Compute Routes)
    const fetchRouteData = async () => {
      setLoading(true);
      setError(null);
      abortControllerRef.current = new AbortController();

      console.log("[useRouteData] Fetching route data:", {
        from: formatCoordinatesForLogging(pharmacyLocation),
        to: formatCoordinatesForLogging(customerLocation),
        routeKey: routeKey.substring(0, 30) + "...",
        needMatrix: needMatrixData,
        needPolylines: needPolylineData,
      });

      try {
        // Fetch Matrix data if needed (distance/duration)
        let matrixElement: RouteMatrixElement | null = cachedMatrixResult;
        if (needMatrixData) {
          console.log("[useRouteData] Fetching Matrix API data");
          const matrixResponse = await deduplicateRequest(routeKey, () =>
            makeRouteMatrixRequest([pharmacyLocation], [customerLocation])
          );

          if (Array.isArray(matrixResponse) && matrixResponse.length > 0) {
            const element = matrixResponse[0];
            if (element.status?.code === 0 || !element.status?.code) {
              matrixElement = element;
              // Cache Matrix data with 30min TTL (existing default)
              routeCache.set(routeKey, element);
              console.log("[useRouteData] Matrix API data cached");
            } else {
              throw new GoogleRoutesError(
                `Route calculation failed with status: ${element.status?.code}`
              );
            }
          } else {
            throw new GoogleRoutesError("Invalid response from Matrix API");
          }
        }

        // Fetch polyline data if needed
        let polylineCoordinates: [number, number][] | undefined =
          cachedPolylineResult?.decodedCoordinates;
        if (needPolylineData && polylineKey) {
          console.log(
            "[useRouteData] Fetching Compute Routes API data (polylines)"
          );
          try {
            const encodedPolyline = await deduplicateRequest(polylineKey, () =>
              makeComputeRoutesRequest(pharmacyLocation, customerLocation)
            );

            if (encodedPolyline) {
              const decodedCoordinates = decodePolyline(encodedPolyline);
              if (validatePolyline(decodedCoordinates)) {
                polylineCoordinates = decodedCoordinates;
                // Cache polyline data with 60min TTL (longer for expensive data)
                routeCache.set(polylineKey, {
                  decodedCoordinates,
                  timestamp: Date.now(),
                  ttl: 60 * 60 * 1000, // 60 minutes
                });
                console.log("[useRouteData] Polyline data cached");
              } else {
                console.warn("[useRouteData] Invalid polyline data received");
              }
            } else {
              console.warn("[useRouteData] No polyline data received");
            }
          } catch (polylineError: any) {
            // Polyline failures are non-critical
            console.warn(
              "[useRouteData] Polyline fetch failed (non-critical):",
              polylineError.message || polylineError
            );
          }
        }

        // Combine results
        const pharmacyToCustomer = {
          distance: matrixElement?.distanceMeters
            ? parseDistance(matrixElement.distanceMeters)
            : undefined,
          duration: matrixElement?.duration
            ? parseDuration(matrixElement.duration)
            : undefined,
          shape: polylineCoordinates,
        };

        setRouteData({ pharmacyToCustomer });
        console.log("[useRouteData] Route data assembled:", {
          distance: pharmacyToCustomer.distance,
          duration: pharmacyToCustomer.duration,
          hasPolylines: !!pharmacyToCustomer.shape,
        });
      } catch (err: any) {
        if (err.name === "AbortError") {
          console.log("[useRouteData] Request cancelled");
          return; // Request was cancelled
        }

        console.error("[useRouteData] Route fetch failed:", {
          error: err.message,
          statusCode: err.statusCode,
          routeKey: routeKey.substring(0, 30) + "...",
        });
        setError(err.message || "Failed to fetch route data");
        setRouteData({});
      } finally {
        setLoading(false);
        abortControllerRef.current = null;
      }
    };

    fetchRouteData();

    // Cleanup function
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [
    routeKey,
    polylineKey,
    pharmacyLocation,
    customerLocation,
    archived,
    includePolylines,
  ]);

  return { routeData, loading, error };
};

export default useRouteData;
